import React, { useCallback, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  HighQuality as HighQualityIcon,
  Image as ImageIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import {
  canvasToBlob,
  getEffectiveScale,
  getRecommendedExportType,
  loadImageFromFile,
  upscaleImageAsync,
} from "../../utils/imageUpscale";

const SCALE_OPTIONS = [1, 2, 4, 8, 16];
const MAX_FILE_BYTES = 16 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";

function formatDims(w, h) {
  return `${w} × ${h}`;
}

function ImageCompareSlider({ beforeSrc, afterSrc, beforeLabel, afterLabel }) {
  const containerRef = useRef(null);
  const [position, setPosition] = useState(50);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.max(2, Math.min(98, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromClientX(e.clientX);
  };

  const onPointerMove = (e) => {
    if (!dragging.current) return;
    updateFromClientX(e.clientX);
  };

  const onPointerUp = () => {
    dragging.current = false;
  };

  const imgSx = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: 320, sm: 420, md: 520 },
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#0f172a",
        userSelect: "none",
        touchAction: "none",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <Box component="img" src={afterSrc} alt="After" sx={imgSx} />
      <Box
        component="img"
        src={beforeSrc}
        alt="Before"
        sx={{
          ...imgSx,
          clipPath: `inset(0 ${100 - position}% 0 0)`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: `${position}%`,
          width: 3,
          ml: "-1.5px",
          bgcolor: "#fff",
          boxShadow: "0 0 12px rgba(0,0,0,0.5)",
          cursor: "ew-resize",
          zIndex: 2,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: `${position}%`,
          transform: "translate(-50%, -50%)",
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: "#fff",
          border: "2px solid #7c3aed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 3,
          boxShadow: 2,
          pointerEvents: "none",
        }}
      >
        <Typography variant="caption" fontWeight={700} color="#7c3aed">
          ⇔
        </Typography>
      </Box>
      <Chip
        label={`Before ${beforeLabel}`}
        size="small"
        sx={{
          position: "absolute",
          top: 12,
          left: 12,
          bgcolor: "rgba(15,23,42,0.75)",
          color: "#fff",
          fontWeight: 600,
        }}
      />
      <Chip
        label={`After ${afterLabel}`}
        size="small"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          bgcolor: "rgba(124,58,237,0.9)",
          color: "#fff",
          fontWeight: 600,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          bottom: 12,
          left: "50%",
          transform: "translateX(-50%)",
          color: "rgba(255,255,255,0.7)",
        }}
      >
        Drag to compare
      </Typography>
    </Box>
  );
}

const LD2HDUpscaler = () => {
  const fileInputRef = useRef(null);
  const [sourceFile, setSourceFile] = useState(null);
  const [sourceImage, setSourceImage] = useState(null);
  const [beforeUrl, setBeforeUrl] = useState(null);
  const [afterUrl, setAfterUrl] = useState(null);
  const [sourceDims, setSourceDims] = useState(null);
  const [outputDims, setOutputDims] = useState(null);
  const [scale, setScale] = useState(4);
  const [enhance, setEnhance] = useState(true);
  const [enhanceMode, setEnhanceMode] = useState("auto");
  const [strongDenoise, setStrongDenoise] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [lastEffectiveScale, setLastEffectiveScale] = useState(null);
  const [lastIsLogo, setLastIsLogo] = useState(false);
  const [exportExt, setExportExt] = useState("png");

  const revokeIfBlob = (url) => {
    if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
  };

  const resetOutput = useCallback(() => {
    revokeIfBlob(afterUrl);
    setAfterUrl(null);
    setOutputDims(null);
    setLastEffectiveScale(null);
    setStatus("");
  }, [afterUrl]);

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    resetOutput();

    if (!file.type.startsWith("image/")) {
      setError("Please upload JPG, PNG, or WebP only.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError("File must be 16 MB or smaller.");
      return;
    }

    try {
      const img = await loadImageFromFile(file);
      revokeIfBlob(beforeUrl);
      const url = URL.createObjectURL(file);
      setSourceFile(file);
      setSourceImage(img);
      setBeforeUrl(url);
      setSourceDims({
        w: img.naturalWidth,
        h: img.naturalHeight,
      });
      setStatus("Image ready — choose scale and click Upscale.");
    } catch {
      setError("Could not read this image.");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const runUpscale = async () => {
    if (!sourceImage || !sourceDims) {
      setError("Upload an image first.");
      return;
    }

    setProcessing(true);
    setError("");
    resetOutput();

    try {
      await new Promise((r) => requestAnimationFrame(r));

      const effective = getEffectiveScale(sourceDims.w, sourceDims.h, scale);
      if (effective < scale) {
        setError(
          `Output capped at 8192px per side — using ${effective}x instead of ${scale}x.`,
        );
      }

      const { canvas, effectiveScale, width, height, isLogo } =
        await upscaleImageAsync(sourceImage, scale, enhance, enhanceMode, {
          strongDenoise,
        });

      const exportType = getRecommendedExportType(
        sourceFile?.type,
        isLogo,
        enhance,
      );
      const ext =
        exportType === "image/jpeg"
          ? "jpg"
          : exportType === "image/webp"
            ? "webp"
            : "png";

      revokeIfBlob(afterUrl);
      const blob = await canvasToBlob(
        canvas,
        exportType,
        exportType === "image/jpeg" ? 0.98 : 0.95,
      );
      const outUrl = URL.createObjectURL(blob);
      setAfterUrl(outUrl);
      setOutputDims({ w: width, h: height });
      setLastEffectiveScale(effectiveScale);
      setLastIsLogo(isLogo);
      setExportExt(ext);
      const modeLabel = isLogo ? "logo/graphics" : "photo";
      setStatus(
        effectiveScale > 1
          ? `Upscaled ${effectiveScale}x (${modeLabel}${strongDenoise ? ", strong denoise" : ""}). Download as .${ext} for best edges.`
          : `Enhanced (${modeLabel}${strongDenoise ? ", strong denoise" : ""}). Download as .${ext}.`,
      );
    } catch (err) {
      console.error(err);
      setError("Upscale failed. Try a smaller image or lower scale.");
    } finally {
      setProcessing(false);
    }
  };

  const downloadResult = () => {
    if (!afterUrl || !sourceFile) return;
    const a = document.createElement("a");
    a.href = afterUrl;
    a.download = `ld2hd-${lastEffectiveScale || scale}x-${Date.now()}.${exportExt}`;
    a.click();
  };

  const clearAll = () => {
    revokeIfBlob(beforeUrl);
    revokeIfBlob(afterUrl);
    setSourceFile(null);
    setSourceImage(null);
    setBeforeUrl(null);
    setAfterUrl(null);
    setSourceDims(null);
    setOutputDims(null);
    setStatus("");
    setError("");
    setLastEffectiveScale(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const previewAfter = afterUrl || beforeUrl;
  const showCompare = Boolean(beforeUrl && afterUrl);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <HighQualityIcon sx={{ fontSize: 36, color: "#7c3aed" }} />
            <Typography variant="h4" fontWeight={800}>
              LD2HD
            </Typography>
            <Chip
              label="Phase 1 — 100% Free"
              color="success"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          </Stack>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Low Definition to High Definition — upscale photos up to 16x with no
            premium limits.
          </Typography>
        </Box>
        {sourceFile && (
          <Button
            startIcon={<RestartAltIcon />}
            variant="outlined"
            onClick={clearAll}
          >
            New image
          </Button>
        )}
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4} lg={3.5}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Upload image
                </Typography>
                <Paper
                  variant="outlined"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    borderStyle: "dashed",
                    borderColor: "#c4b5fd",
                    bgcolor: "#faf5ff",
                    "&:hover": { bgcolor: "#f3e8ff" },
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 40, color: "#7c3aed", mb: 1 }} />
                  <Typography fontWeight={600}>+ Upload Image</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    JPG, JPEG, PNG, WebP — up to 16 MB
                  </Typography>
                  {sourceFile && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {sourceFile.name}
                    </Typography>
                  )}
                </Paper>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT}
                  hidden
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Upscale image
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Super Resolution (browser — free Phase 1)
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Upscale to
                </Typography>
                <ButtonGroup fullWidth size="small" sx={{ mb: 2 }}>
                  {SCALE_OPTIONS.map((s) => (
                    <Button
                      key={s}
                      variant={scale === s ? "contained" : "outlined"}
                      onClick={() => setScale(s)}
                      sx={
                        scale === s
                          ? {
                              bgcolor: "#7c3aed",
                              "&:hover": { bgcolor: "#6d28d9" },
                            }
                          : {}
                      }
                    >
                      {s}x
                    </Button>
                  ))}
                </ButtonGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enhance}
                      onChange={(e) => setEnhance(e.target.checked)}
                      color="secondary"
                    />
                  }
                  label="Enhance quality (Lanczos + sharpen)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={strongDenoise}
                      onChange={(e) => setStrongDenoise(e.target.checked)}
                      color="secondary"
                      disabled={!enhance}
                    />
                  }
                  label="Strong denoise (photos with heavy grain/blur)"
                  sx={{ mt: 0.5 }}
                />
                <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                  <InputLabel id="ld2hd-mode-label">Enhance mode</InputLabel>
                  <Select
                    labelId="ld2hd-mode-label"
                    label="Enhance mode"
                    value={enhanceMode}
                    onChange={(e) => setEnhanceMode(e.target.value)}
                  >
                    <MenuItem value="auto">Auto (detect logo vs photo)</MenuItem>
                    <MenuItem value="logo">Logo / Flex / Graphics</MenuItem>
                    <MenuItem value="photo">Photo / Portrait</MenuItem>
                  </Select>
                </FormControl>
                <Alert severity="info" sx={{ mt: 1.5 }} icon={false}>
                  Logos & flex boards: use <strong>Logo</strong> mode and download{" "}
                  <strong>PNG</strong>. JPG blurs sharp edges when you zoom in.
                </Alert>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={!sourceImage || processing}
                  onClick={runUpscale}
                  sx={{
                    mt: 2,
                    py: 1.25,
                    bgcolor: "#7c3aed",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "#6d28d9" },
                  }}
                >
                  {processing ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Upscale"
                  )}
                </Button>
                {status && (
                  <Alert severity="success" sx={{ mt: 2 }} icon={false}>
                    {status}
                  </Alert>
                )}
                {error && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ bgcolor: "#f8fafc" }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Enhance further
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Phase 2: AI sharpen, deblur, dehaze — coming soon. Phase 1 uses
                  high-quality stepped upscale + optional enhance.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} md={8} lg={8.5}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              {!beforeUrl ? (
                <Box
                  sx={{
                    height: { xs: 280, md: 480 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.secondary",
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <ImageIcon sx={{ fontSize: 64, opacity: 0.35, mb: 2 }} />
                  <Typography fontWeight={600}>Preview will appear here</Typography>
                  <Typography variant="body2">
                    Upload an image, pick 4x / 8x / 16x, then Upscale
                  </Typography>
                </Box>
              ) : showCompare && sourceDims && outputDims ? (
                <ImageCompareSlider
                  beforeSrc={beforeUrl}
                  afterSrc={afterUrl}
                  beforeLabel={formatDims(sourceDims.w, sourceDims.h)}
                  afterLabel={formatDims(outputDims.w, outputDims.h)}
                />
              ) : (
                <Box
                  component="img"
                  src={previewAfter}
                  alt="Preview"
                  sx={{
                    width: "100%",
                    maxHeight: { xs: 320, md: 520 },
                    objectFit: "contain",
                    borderRadius: 2,
                    bgcolor: "#0f172a",
                  }}
                />
              )}

              <Divider sx={{ my: 2 }} />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Typography variant="body2" color="text.secondary">
                  {sourceDims
                    ? `Source: ${formatDims(sourceDims.w, sourceDims.h)}`
                    : "No image loaded"}
                  {outputDims
                    ? ` → Output: ${formatDims(outputDims.w, outputDims.h)}`
                    : ""}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  disabled={!afterUrl}
                  onClick={downloadResult}
                  sx={{
                    bgcolor: "#7c3aed",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "#6d28d9" },
                  }}
                >
                  Download
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LD2HDUpscaler;
